// src/utils/apiClient.ts
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import config from '@/config'
import { useCompanyStore } from '@/stores/companyStore'
import { useAuthStore } from '@/stores/auth'
import { router } from '@/router'
import { ElNotification } from 'element-plus'

interface User {
    token: string
}

const apiClient: AxiosInstance = axios.create({
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 0,
})
apiClient.defaults.baseURL = import.meta.env.VITE_API_URL
config.baseURL = import.meta.env.VITE_BASE_URL

apiClient.interceptors.request.use(
    (requestConfig: AxiosRequestConfig) => {
        // Detect FormData and override headers
        if (requestConfig.data instanceof FormData) {
            if (!requestConfig.headers) requestConfig.headers = {}
            // Let the browser set the right multipart/form-data boundary
            delete requestConfig.headers['Content-Type']
        } else {
            if (!requestConfig.headers) requestConfig.headers = {}
            requestConfig.headers['Content-Type'] = 'application/json'
        }

        // ---- Token handling ----
        let token: string | null = null
        const user = localStorage.getItem('user')

        if (user) {
            try {
                const parsedUser: User = JSON.parse(user)
                token = parsedUser.token
            } catch {
                token = null
            }
        }

        if (!token) {
            token = localStorage.getItem('hr_token')
        }

        if (token) {
            requestConfig.headers['Authorization'] = `Bearer ${token}`
        } else {
            delete requestConfig.headers['Authorization']
        }

        // ---- CompanyID handling ----
        const companyStore = useCompanyStore()
        const companyID = companyStore.companyID || localStorage.getItem('companyID')
        if (companyID) {
            requestConfig.headers['CompanyID'] = companyID.toString()
        }

        return requestConfig
    },
    error => Promise.reject(error),
)

// ✅ Enhanced response interceptor to handle device logout
apiClient.interceptors.response.use(
    response => response,
    error => {
        // ✅ Handle 401 Unauthorized errors
        if (error.response?.status === 401) {
            const errorMessage = error.response?.data?.message || ''
            const currentPath = router.currentRoute.value.path

            // ✅ Skip if already on login page
            if (currentPath === '/auth/login') {
                return Promise.reject(error)
            }

            // ✅ Check if it's a device logout
            const isDeviceLogout =
                errorMessage.toLowerCase().includes('device') || errorMessage.toLowerCase().includes('logged out')

            if (isDeviceLogout) {
                // ✅ Device was logged out remotely
                const authStore = useAuthStore()
                authStore.logout(false) // Don't show success notification

                ElNotification.warning({
                    title: 'Device Logged Out',
                    message: errorMessage || 'This device has been logged out. Please log in again.',
                    duration: 5000,
                })
            }
        }

        return Promise.reject(error)
    },
)

export default apiClient
